import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useAppStore } from '@/store/appStore'
import { getAllNotes, saveAllNotes, enqueueNoteChange } from '@/db/viewerDb'
import { syncNotes } from '@/api/notesApi'
import { extractApiErrorMessage } from '@/utils/apiError'
import type { ApiNote, ContentType, NoteChange } from '@/types/api'


/**
 * Hook for reading notes and performing note mutations.
 * Reads from IndexedDB cache. Online mutations go directly to API then re-cache.
 * Offline mutations are queued in the offline-queue store.
 */
export function useNotes() {
  const { t } = useTranslation()
  const { isOffline, userDataSyncVersion, bumpUserDataSyncVersion } = useAppStore()
  const [notes, setNotes] = useState<ApiNote[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Re-read from IndexedDB on mount and whenever useInitialSync writes fresh data
  useEffect(() => {
    getAllNotes().then(setNotes)
  }, [userDataSyncVersion])

  // Get all notes for a specific entity
  const getNotesFor = (contentType: ContentType, entityId: number) =>
    notes.filter((n) => n.contentType === contentType && n.entityId === entityId)

  // Create or update a note
  const saveNote = async (params: {
    id?: number | null
    contentType: ContentType
    entityId: number
    title?: string | null
    body: string
  }) => {
    const clientId = crypto.randomUUID()
    const now = new Date().toISOString()
    const operation: NoteChange['operation'] = params.id ? 'Update' : 'Create'

    const change: NoteChange = {
      clientId,
      id: params.id ?? null,
      operation,
      contentType: params.contentType,
      entityId: params.entityId,
      title: params.title,
      body: params.body,
      clientUpdatedOn: now,
    }

    if (isOffline) {
      await enqueueNoteChange(change)
      // Optimistically update local state
      if (operation === 'Create') {
        const optimisticNote: ApiNote = {
          id: -(Date.now()), // Negative temp ID
          contentType: params.contentType,
          entityId: params.entityId,
          title: params.title ?? null,
          body: params.body,
          updatedOn: now,
        }
        const updated = [...notes, optimisticNote]
        await saveAllNotes(updated)
        setNotes(updated)
      } else {
        const updated = notes.map((n) =>
          n.id === params.id
            ? { ...n, body: params.body, title: params.title ?? n.title, updatedOn: now }
            : n
        )
        await saveAllNotes(updated)
        setNotes(updated)
      }
      bumpUserDataSyncVersion()
      toast.info(operation === 'Create' ? t('notes.noteSavedOffline') : t('notes.noteUpdatedOffline'))
      return
    }

    // Online: sync immediately
    setIsSaving(true)
    try {
      const response = await syncNotes({ changes: [change] })
      await saveAllNotes(response.notes)
      setNotes(response.notes)
      bumpUserDataSyncVersion()
      toast.success(operation === 'Create' ? t('notes.noteSaved') : t('notes.noteUpdated'))
    } catch (err) {
      toast.error(extractApiErrorMessage(err) || t('notes.noteSaveFailed'))
      throw err
    } finally {
      setIsSaving(false)
    }
  }

  // Delete a note
  const deleteNote = async (id: number) => {
    const now = new Date().toISOString()
    const change: NoteChange = {
      clientId: crypto.randomUUID(),
      id,
      operation: 'Delete',
      clientUpdatedOn: now,
    }

    if (isOffline) {
      await enqueueNoteChange(change)
      const updated = notes.filter((n) => n.id !== id)
      await saveAllNotes(updated)
      setNotes(updated)
      bumpUserDataSyncVersion()
      toast.info(t('notes.noteDeletedOffline'))
      return
    }

    setIsDeleting(true)
    try {
      const response = await syncNotes({ changes: [change] })
      await saveAllNotes(response.notes)
      setNotes(response.notes)
      bumpUserDataSyncVersion()
      toast.success(t('notes.noteDeleted'))
    } catch (err) {
      toast.error(extractApiErrorMessage(err) || t('notes.noteDeleteFailed'))
      throw err
    } finally {
      setIsDeleting(false)
    }
  }

  return { notes, getNotesFor, saveNote, deleteNote, isSaving, isDeleting }
}
